// src/components/DashboardTabs/PricingTab.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { api } from "../../services/api";
import FinlandRegionMap from "../FinlandRegionMap/FinlandRegionMap.jsx";

// --- Nopea autosave ---
const DEBOUNCE_MS = 700; // niputtaa näppäilyt yhteen HTTP-kutsuun

// --- Hinnan rajat ---
const MAX_PRICE_EUR = 8000; // Yläraja yhdelle setille (€)

// -----------------------------
// Konfigit ja apurit
// -----------------------------

// Yleisimmät setit pudotusvalikkoon
const COMMON_SETS = [
  // --- 120 min (uudet) ---
  { set_count: 4, minutes_per_set: 120, label: "4x120min" },
  { set_count: 3, minutes_per_set: 120, label: "3x120min" },
  { set_count: 2, minutes_per_set: 120, label: "2x120min" },
  { set_count: 1, minutes_per_set: 120, label: "1x120min" },

  // --- 90 min ---
  { set_count: 2, minutes_per_set: 90, label: "2x90min" },
  { set_count: 1, minutes_per_set: 90, label: "1x90min" },

  // --- 60 min ---
  { set_count: 4, minutes_per_set: 60, label: "4x60min" },
  { set_count: 3, minutes_per_set: 60, label: "3x60min" },
  { set_count: 2, minutes_per_set: 60, label: "2x60min" },
  { set_count: 1, minutes_per_set: 60, label: "1x60min" },

  // --- 45 min ---
  { set_count: 4, minutes_per_set: 45, label: "4x45min" },
  { set_count: 3, minutes_per_set: 45, label: "3x45min" },
  { set_count: 2, minutes_per_set: 45, label: "2x45min" },
  { set_count: 1, minutes_per_set: 45, label: "1x45min" },

  // --- 30 min ---
  { set_count: 4, minutes_per_set: 30, label: "4x30min" },
  { set_count: 3, minutes_per_set: 30, label: "3x30min" },
  { set_count: 2, minutes_per_set: 30, label: "2x30min" },
  { set_count: 1, minutes_per_set: 30, label: "1x30min" },
];

// “2x45”, “2x45min”, “2 45”, “245” → { set_count, minutes_per_set, label }
function parseSetInput(s) {
  const t = String(s || "").toLowerCase().replace(/\s+/g, "").replace("min", "");
  const m = t.match(/^(\d+)[x×](\d+)$/) || t.match(/^(\d+)(\d+)$/);
  if (!m) return null;
  const count = +m[1],
    minutes = +m[2];
  if (!(count > 0 && minutes > 0)) return null;
  return { set_count: count, minutes_per_set: minutes, label: `${count}x${minutes}min` };
}

// "2x45", "2x45min", "2 45" → "2x45min"
function canonLabel(s) {
  const t = String(s || "").toLowerCase().replace(/\s+/g, "").replace("min", "");
  const m = t.match(/^(\d+)[x×](\d+)$/) || t.match(/^(\d+)(\d+)$/);
  if (!m) return String(s || "").trim();
  const count = +m[1],
    minutes = +m[2];
  if (!(count > 0 && minutes > 0)) return String(s || "").trim();
  return `${count}x${minutes}min`;
}

// -----------------------------
// SetSelector (haku + ehdotukset; EI oman setin luontia)
// -----------------------------
function SetSelector({ existingLabels, onPick, loadOptions }) {
  const [q] = useState(""); // ei erillistä hakua; pidetään tyhjänä
  const [opts, setOpts] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await loadOptions(q);
        if (!alive) return;
        const seen = new Set();
        const uniq = (list || []).filter((o) => {
          if (!o?.label) return false;
          if (seen.has(o.label)) return false;
          seen.add(o.label);
          return true;
        });
        setOpts(uniq);
      } catch {
        setOpts([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [q, loadOptions]);

  const groups = opts.reduce((acc, s) => {
    const key = Number(s?.minutes_per_set) > 0 ? `${s.minutes_per_set} min` : "Muut";
    (acc[key] ||= []).push(s);
    return acc;
  }, {});

  return (
    <div className="w-full p-2">
      <p className="mb-2 text-xs md:text-lg text-black/80 dark:text-white/80">
        Lisää setti maakuntaan pudotusvalikosta.
      </p>
      <div className="w-32 md:w-64 lg:w-72">
        <select
          className="h-10 w-full rounded-lg px-3
             bg-white dark:bg-black/40
             border border-black/10 dark:border-white/10 truncate
             text-xs md:text-lg leading-tight"
          defaultValue=""
          onChange={(e) => {
            const value = e.target.value;
            if (!value) return;
            const opt = e.target.selectedOptions[0];
            onPick({
              label: value,
              set_count: Number(opt.getAttribute("data-count") || 0),
              minutes_per_set: Number(opt.getAttribute("data-mins") || 0),
            });
            e.target.value = "";
          }}
        >
          <option value="">Valitse..</option>
          {Object.entries(groups).map(([label, arr]) => (
            <optgroup key={label} label={label}>
              {arr.map((s) => {
                const dup = existingLabels.has(s.label);
                return (
                  <option
                    key={s.label}
                    value={s.label}
                    data-count={s.set_count}
                    data-mins={s.minutes_per_set}
                    disabled={dup}
                  >
                    {s.label}
                    {dup ? " — jo listalla" : ""}
                  </option>
                );
              })}
            </optgroup>
          ))}
        </select>
      </div>
    </div>
  );
}

SetSelector.propTypes = {
  existingLabels: PropTypes.instanceOf(Set).isRequired,
  onPick: PropTypes.func.isRequired,
  loadOptions: PropTypes.func.isRequired,
};

// -----------------------------
// Pääkomponentti
// -----------------------------
export default function PricingTab({ band, exposeSave, onSaved }) {
  const labelCls =
    "block dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-lg mb-1";
  const inputCls =
    "w-full h-10 rounded-lg px-3 bg-white dark:bg-black/40 border border-black/10 dark:border-white/10";

  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [sets, setSets] = useState([]);
  const [priceMap, setPriceMap] = useState({});
  const [suggestedSets, setSuggestedSets] = useState(COMMON_SETS);
  const [saving, setSaving] = useState(false);
  const changeTimerRef = useRef(null);

  // ref tuoreelle priceMapille (autosave käyttää tätä)
  const priceMapRef = useRef({});
  useEffect(() => {
    priceMapRef.current = priceMap;
  }, [priceMap]);

  // apu-refit
  const labelIdCacheRef = useRef(new Map()); // label → band_set_id
  const savedSnapshotRef = useRef({}); // { [regionId]: { [label]: number } }
  const regionAbortRef = useRef(new Map()); // regionId → AbortController

  const dirtyRef = useRef(false);
  const hydratingRef = useRef(false);
  const selectedRegionRef = useRef(null);
  const userPickedRegionRef = useRef(false);
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  const regionId = selectedRegion?.id ?? null;
  const regionName = selectedRegion?.name || "";

  const toNum = (v) => {
    const s = String(v ?? "").replace(",", ".").trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const regionPrices = useMemo(
    () => ((regionId != null ? priceMap[regionId] : null) || {}),
    [priceMap, regionId]
  );

  const existingLabelsRegion = useMemo(
    () => new Set(Object.keys(regionPrices || {})),
    [regionPrices]
  );

  const regionsWithPrices = useMemo(() => {
    const out = [];
    for (const r of regions) {
      const rp = priceMap[r.id] || {};
      const cnt = Object.values(rp).reduce((acc, v) => acc + (toNum(v) != null ? 1 : 0), 0);
      if (cnt > 0) out.push({ ...r, count: cnt });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name, "fi"));
  }, [regions, priceMap]);

  const hasPrices = regionsWithPrices.length > 0;

  const allRegionsCovered = useMemo(() => {
    if (!Array.isArray(regions) || regions.length === 0) return false;
    return regions.every((r) => {
      const rp = priceMap[r.id] || {};
      return Object.values(rp).some((v) => toNum(v) != null);
    });
  }, [regions, priceMap]);

  useEffect(() => {
    if (hydratingRef.current) return;
    dirtyRef.current = true;
  }, [sets, priceMap, selectedRegion?.id]);

  useEffect(() => {
    selectedRegionRef.current = selectedRegion;
  }, [selectedRegion]);

  // -----------------------------
  // Alustukset
  // -----------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await api.pricing.regions();
        if (!alive) return;
        const clean = (Array.isArray(list) ? list : [])
          .map((r) => ({
            id: Number(r.id ?? r.region_id ?? r.value),
            name: r.name ?? r.region_name ?? r.label,
            slug: r.slug ?? null,
          }))
          .filter((r) => Number.isFinite(r.id) && r.name);
        setRegions(clean);
      } catch (e) {
        console.error("regions load fail", e);
        toast.error("Maakuntien haku epäonnistui.", { containerId: "global" });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const labels = await api.pricing.setLabels(band.id); // → ["2x45min", ...]
        if (!alive) return;
        const parsed = (labels || []).map(parseSetInput).filter(Boolean);
        const seen = new Set();
        const all = [...COMMON_SETS, ...parsed].filter((x) => {
          if (!x?.label || seen.has(x.label)) return false;
          seen.add(x.label);
          return true;
        });
        setSuggestedSets(all);
      } catch {
        setSuggestedSets(COMMON_SETS);
      }
    })();
    return () => {
      alive = false;
    };
  }, [band?.id]);

  // Hydraus band-propista (vain kun id vaihtuu / mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!band?.id) return;

    hydratingRef.current = true;

    const srcSets = Array.isArray(band.sets) ? band.sets : [];
    const rows = srcSets
      .map((s, idx) => {
        const def = s.set_def || s.set || {};
        const set_count = Number(s.set_count ?? def.set_count ?? 0) || 0;
        const minutes_per_set = Number(s.minutes_per_set ?? def.minutes_per_set ?? 0) || 0;
        const rawLabel =
          s.label ??
          s.set_label ??
          def.label ??
          (set_count && minutes_per_set ? `${set_count}x${minutes_per_set}min` : "");
        const label = canonLabel(rawLabel);
        const position = Number.isFinite(+s.position) ? +s.position : idx + 1;
        return {
          id: s.id ?? s.band_set_id ?? null,
          label,
          set_count,
          minutes_per_set,
          position,
        };
      })
      .sort((a, b) => a.position - b.position);

    setSets(rows);

    // Täytä cache
    const m = new Map();
    rows.forEach((r) => {
      if (r.id && r.label) m.set(r.label, r.id);
    });
    labelIdCacheRef.current = m;

    // hinnat propista
    const rebuilt = {};
    const items = Array.isArray(band.pricing) ? band.pricing : [];
    for (const p of items) {
      const rid = Number(p.region_id);
      const lbl = canonLabel(p.set_label ?? "");
      const price = p.base_price ?? p.price;
      if (!Number.isFinite(rid) || !lbl) continue;
      if (!rebuilt[rid]) rebuilt[rid] = {};
      rebuilt[rid][lbl] = price != null ? String(price) : "";
    }
    setPriceMap(rebuilt);

    // snapshot (vain kirjanpitoa varten)
    const snap = {};
    for (const [ridStr, map] of Object.entries(rebuilt)) {
      const rid = Number(ridStr);
      const m2 = {};
      for (const [lbl, raw] of Object.entries(map)) {
        const n = toNum(raw);
        if (n != null) m2[lbl] = n;
      }
      snap[rid] = m2;
    }
    savedSnapshotRef.current = snap;

    if (typeof queueMicrotask === "function") {
      queueMicrotask(() => {
        hydratingRef.current = false;
        dirtyRef.current = false;
      });
    } else {
      setTimeout(() => {
        hydratingRef.current = false;
        dirtyRef.current = false;
      }, 0);
    }
  }, [band?.id]);

  // Autovalinta
  useEffect(() => {
    if (!regions.length) {
      setSelectedRegion(null);
      userPickedRegionRef.current = false;
      return;
    }
    if (userPickedRegionRef.current) return;
    const currentSel = selectedRegionRef.current;
    if (!currentSel) {
      setSelectedRegion(regionsWithPrices[0] ?? regions[0]);
    } else {
      const inList = regions.some((r) => r.id === currentSel.id);
      if (!inList) setSelectedRegion(regionsWithPrices[0] ?? regions[0]);
    }
  }, [regions, regionsWithPrices]);

  // SetSelectorin datalähde
  const loadSetOptions = useCallback(
    async (q) => {
      const needle = String(q || "").trim().toLowerCase().replace(/\s+/g, "");
      const base = (suggestedSets || []).filter((s) =>
        String(s.label).toLowerCase().replace(/\s+/g, "").includes(needle)
      );
      const seen = new Set();
      const out = base.filter((x) => {
        if (!x?.label || seen.has(x.label)) return false;
        seen.add(x.label);
        return true;
      });
      return out.slice(0, 30);
    },
    [suggestedSets]
  );

  // -----------------------------
  // Muokkausapuja
  // -----------------------------
  const priceEditRef = useRef({});
  const skipToastRef = useRef(false);
  const priceKey = (lbl) => `${regionId ?? "noRegion"}::${lbl}`;

  // --- 1) Varmista setti-ID:t ---
  const ensureSetsSaved = useCallback(async () => {
    const known = labelIdCacheRef.current;
    const normalizedLabels = (sets || [])
      .map((s) => {
        const sc = Number(String(s.set_count).replace(",", ".")) || 0;
        const mp = Number(String(s.minutes_per_set).replace(",", ".")) || 0;
        return sc && mp ? `${sc}x${mp}min` : s.label || "";
      })
      .filter(Boolean);
    const missing = normalizedLabels.some((lbl) => !known.has(lbl));
    const normalized = (sets || [])
      .map((s, idx) => {
        const set_count = Number(String(s.set_count).replace(",", ".")) || 0;
        const minutes_per_set = Number(String(s.minutes_per_set).replace(",", ".")) || 0;
        const position = Number.isFinite(+s.position) ? +s.position : idx;
        const label = canonLabel(
          set_count && minutes_per_set ? `${set_count}x${minutes_per_set}min` : s.label || ""
        );
        return { id: s.id ?? null, set_count, minutes_per_set, position, label };
      })
      .filter((s) => s.set_count && s.minutes_per_set && s.label);

    try {
      if (!missing) return new Map(known);
      let res;
      if (typeof api.sets?.save === "function") {
        res = await api.sets.save(band.id, normalized, { replaceAll: true });
      } else if (typeof api.admin?.sets?.save === "function") {
        res = await api.admin.sets.save(band.id, normalized, { replaceAll: true });
      }
      const items = Array.isArray(res?.items) ? res.items : Array.isArray(res) ? res : [];
      const rows = items
        .map((s, i) => ({
          id: s.id ?? s.band_set_id ?? null,
          label: s.label ?? "",
          set_count: Number(s.set_count ?? 0) || 0,
          minutes_per_set: Number(s.minutes_per_set ?? 0) || 0,
          position: Number.isFinite(+s.position) ? +s.position : i,
        }))
        .sort((a, b) => a.position - b.position);
      if (rows.length) setSets(rows);
      const map = new Map(rows.filter((r) => r.id && r.label).map((r) => [r.label, r.id]));
      labelIdCacheRef.current = new Map([...known, ...map]);
      return new Map(labelIdCacheRef.current);
    } catch {
      return new Map(labelIdCacheRef.current);
    }
  }, [band?.id, sets]);

  // --- 2) Varsinainen tallennus yhdelle maakunnalle (täysi replaceAll) ---
  const saveRegionReplaceAll = useCallback(
    async (rid, overrideRegionMap) => {
      if (rid == null) return;
      try {
        const labelToId = await ensureSetsSaved();
        const allPriceMaps = priceMapRef.current || {};
        const curMap = (overrideRegionMap ?? allPriceMaps[rid]) || {};

        // Rakenna täydellinen items-lista tälle maakunnalle
        const items = Object.entries(curMap)
          .map(([label, raw]) => {
            const n = toNum(raw);
            const sid = labelToId.get(label);
            return sid && n != null
              ? { band_set_id: sid, region_id: rid, price: n }
              : null;
          })
          .filter(Boolean);

        // Peru aiempi saman maakunnan pyyntö
        const old = regionAbortRef.current.get(rid);
        if (old) {
          try {
            old.abort();
          } catch {}
        }
        const ac = new AbortController();
        regionAbortRef.current.set(rid, ac);

        await api.pricing.save(band.id, items, {
          replaceAll: true,
          region_id: rid,
          signal: ac.signal,
        });

        regionAbortRef.current.delete(rid);

        // snapshot talteen (kirjanpitoa varten)
        const snap = {};
        for (const [label, raw] of Object.entries(curMap)) {
          const n = toNum(raw);
          if (n != null) snap[label] = n;
        }
        savedSnapshotRef.current = { ...savedSnapshotRef.current, [rid]: snap };

        // kerrotaan parentille aina, että tämän maakunnan hinnat päivittyi
        onSavedRef.current?.({ replaceRegion: rid });
      } catch (e) {
        console.debug("autosave failed outer", e);
      }
    },
    [band?.id, ensureSetsSaved]
  );

  // autosave-apu
  const saveSoon = useCallback(() => {
    clearTimeout(changeTimerRef.current);
    changeTimerRef.current = setTimeout(() => {
      const currentRegion = selectedRegionRef.current;
      if (currentRegion?.id != null) {
        const rid = currentRegion.id;
        const curMap = priceMapRef.current?.[rid] || {};
        saveRegionReplaceAll(rid, curMap);
      }
    }, DEBOUNCE_MS);
  }, [saveRegionReplaceAll]);

  const saveNow = useCallback(() => {
    clearTimeout(changeTimerRef.current);
    const currentRegion = selectedRegionRef.current;
    if (currentRegion?.id != null) {
      const rid = currentRegion.id;
      const curMap = priceMapRef.current?.[rid] || {};
      return saveRegionReplaceAll(rid, curMap);
    }
    return Promise.resolve();
  }, [saveRegionReplaceAll]);

  const notifyPriceChange = (label, prevRaw, nextRaw) => {
    const prev = toNum(prevRaw);
    const next = toNum(nextRaw);
    if (prev == null && next != null) {
      toast.info(
        `Lisäsit hinnan setille ${label} maakunnassa ${regionName}: ${String(nextRaw).trim()} €.`,
        { containerId: "global" }
      );
    } else if (prev != null && next == null) {
      toast.info(`Poistit hinnan setiltä ${label} maakunnassa ${regionName}.`, {
        containerId: "global",
      });
    } else if (prev != null && next != null && prev !== next) {
      toast.info(
        `Päivitit hinnan setille ${label} maakunnassa ${regionName}: ${prevRaw} € → ${nextRaw} €.`,
        { containerId: "global" }
      );
    }
  };

  // Normalisoi ja rajaa hinnan välille 0 ... MAX_PRICE_EUR
  const clampPrice = (raw) => {
    const s = String(raw ?? "").replace(",", ".").trim();
    if (!s) return ""; // tyhjä = ei hintaa

    const n = Number(s);
    if (!Number.isFinite(n)) return ""; // roska → tyhjennetään
    if (n < 0) return "0"; // ei miinushintoja
    if (n > MAX_PRICE_EUR) return String(MAX_PRICE_EUR); // katto

    return String(n);
  };

  const setRegionPrice = (label, rawVal) => {
    if (regionId == null) return;
    const safeLabel = String(label || "").trim();
    if (!safeLabel) return;

    // Edellinen arvo tälle maakunta+setti -kombolle
    const prevVal = (priceMap[regionId] || {})[safeLabel];
    const prevNum = Number(
      String(prevVal ?? "")
        .replace(",", ".")
        .trim()
    );

    const normalized = String(rawVal ?? "").trim().replace(",", ".");
    const nextNum = Number(normalized);

    const wasOver = Number.isFinite(prevNum) && prevNum > MAX_PRICE_EUR;
    const isOver = normalized && Number.isFinite(nextNum) && nextNum > MAX_PRICE_EUR;

    // Päivitä state (ilman toastia)
    setPriceMap((prev) => {
      const nextRegion = { ...(prev[regionId] || {}) };
      nextRegion[safeLabel] = clampPrice(normalized);
      return { ...prev, [regionId]: nextRegion };
    });

    // Näytä varoitus vain kun hypätään ensimmäistä kertaa rajan yli
    if (isOver && !wasOver) {
      toast.warning(
        `Yksittäisen setin hinta voi olla enintään ${MAX_PRICE_EUR} €. Arvo rajattiin tähän.`,
        { containerId: "global" }
      );
    }
  };

  const removeSetFromRegion = (label) => {
    if (regionId == null) return;
    const safeLabel = String(label || "").trim();
    if (!safeLabel) return;

    setPriceMap((prev) => {
      const cur = prev[regionId] || {};
      if (!(safeLabel in cur)) return prev;

      const nextRegionPrices = { ...cur };
      delete nextRegionPrices[safeLabel];

      const nextMap = { ...prev, [regionId]: nextRegionPrices };

      // jos tästä maakunnasta katosi viimeinen setti → vaihda seuraavaan maakuntaan
      const isNowEmpty = Object.keys(nextRegionPrices).length === 0;
      if (isNowEmpty) {
        const hasAnyPrice = (rid) => {
          const rp = nextMap[rid] || {};
          return Object.values(rp).some((v) => toNum(v) != null);
        };

        const currentIdx = regions.findIndex((r) => r.id === regionId);
        let nextRegionObj = null;

        // Etsi seuraava hinnallinen maakunta listan lopusta eteenpäin
        for (let i = currentIdx + 1; i < regions.length; i++) {
          if (hasAnyPrice(regions[i].id)) {
            nextRegionObj = regions[i];
            break;
          }
        }

        // Jos ei löytynyt eteenpäin, kokeile listan alusta nykyiseen indeksiin asti
        if (!nextRegionObj) {
          for (let i = 0; i < currentIdx; i++) {
            if (hasAnyPrice(regions[i].id)) {
              nextRegionObj = regions[i];
              break;
            }
          }
        }

        userPickedRegionRef.current = true;

        if (nextRegionObj) {
          setSelectedRegion(nextRegionObj);
        } else {
          // ei yhtään muuta hinnallista maakuntaa → tyhjennä valinta
          setSelectedRegion(null);
        }
      }

      // Tallenna heti tämän maakunnan uudet hinnat backendille käyttäen juuri laskettua map:ia
      saveRegionReplaceAll(regionId, nextRegionPrices);

      return nextMap;
    });
  };

  // Poista koko maakunnan hinnat
  const removeRegionCompletely = useCallback(
    async (region) => {
      const rid = Number(region?.id);
      if (!Number.isFinite(rid)) return;
      userPickedRegionRef.current = true;
      setSelectedRegion(region);
      setPriceMap((prev) => ({ ...prev, [rid]: {} }));
      await new Promise((r) =>
        typeof queueMicrotask === "function" ? queueMicrotask(r) : setTimeout(r, 0)
      );
      await saveRegionReplaceAll(rid, {});
      toast.info(`Poistit kaikki hinnat maakunnalta ${region?.name ?? ""}.`, {
        containerId: "global",
      });
    },
    [saveRegionReplaceAll]
  );

  const handlePickSet = (o) => {
    const lbl = o?.label;
    if (!lbl) return;
    setSets((prev) => [
      ...prev,
      {
        label: lbl,
        set_count: Number(o.set_count) || 0,
        minutes_per_set: Number(o.minutes_per_set) || 0,
        position: prev.length,
      },
    ]);

    setPriceMap((prev) => {
      if (regionId == null || !lbl) return prev;
      const nextRegion = { ...(prev[regionId] || {}) };
      if (!(lbl in nextRegion)) nextRegion[lbl] = "";
      return { ...prev, [regionId]: nextRegion };
    });

    toast.info(
      `Lisäsit setin ${lbl}. Lisää hinta maakunnalle ${regionName} ja tallenna lopuksi.`,
      { containerId: "global" }
    );
  };

  // -----------------------------
  // Tallennus
  // -----------------------------
  const onSave = useCallback(async () => {
    if (saving) return;

    setSaving(true);
    try {
      await ensureSetsSaved();
      if (regionId != null) {
        const curMap = priceMapRef.current?.[regionId] || {};
        await saveRegionReplaceAll(regionId, curMap);
      } else {
        onSavedRef.current?.({});
      }
      toast.success(
        regionId != null ? "Setit ja hinnat tallennettu." : "Setit tallennettu.",
        { containerId: "global" }
      );
      dirtyRef.current = false;
    } catch (e) {
      console.error(e);
      toast.error("Tallennus epäonnistui.", { containerId: "global" });
    } finally {
      setSaving(false);
    }
  }, [saving, regionId, ensureSetsSaved, saveRegionReplaceAll]);

  // Rekisteröi tallennus sticky action barille
  useEffect(() => {
    exposeSave?.(onSave);
    return () => exposeSave?.(null);
  }, [exposeSave, onSave]);

  // -----------------------------
  // UI
  // -----------------------------
  const safePickRegion = (next) => {
    userPickedRegionRef.current = true;
    setSelectedRegion(next);
  };

  return (
    <form className="space-y-6 p-4" onSubmit={(e) => e.preventDefault()}>
      {/* Maakunta-chipit + valinnat + kartta */}
      <div className="grid gap-3 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] md:items-start">
        {/* Vasen sarake: chipit + setti (desktopissa) */}
        <div className="space-y-3">
          {/* Chipit */}
          <div className="rounded-xl bg-white/70 dark:bg-black/40 ring-1 ring-black/10 dark:ring-white/10 p-3">
            <h4 className="font-customFont1 text-xs md:text-lg font-semibold mb-2 dark:text-white/80 text-black/80">
              {hasPrices ? (
                allRegionsCovered ? (
                  <>
                    Olet määrittänyt hinnat kaikkiin maakuntiin. Profiilissa keikkailualueeksi
                    näytetään Koko Suomi. Jos poistat hinnan yhdestäkin maakunnasta niin näet
                    kaikki maakunnat tässä ja ne luetteloituvat taas profiilissasi
                  </>
                ) : (
                  <>Olet määrittänyt hintoja seuraaviin maakuntiin: (maakunta/settejä)</>
                )
              ) : (
                <>Valitse maakunta, lisää setti pudotusvalikosta ja anna hinta – lopuksi tallenna.</>
              )}
            </h4>

            <div className="flex flex-wrap gap-1 md:gap-2">
              {allRegionsCovered ? (
                <span
                  className="inline-flex items-center select-none overflow-hidden
                    pl-3 pr-3 py-1.5 rounded-full text-xs md:text-lg font-medium
                    bg-pink text-black ring-2 ring-transparent opacity-90"
                  title="Hinnat on määritetty kaikkiin maakuntiin"
                >
                  Koko Suomi
                </span>
              ) : regionsWithPrices.length ? (
                regionsWithPrices.map((r) => {
                  const active = selectedRegion?.id === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => safePickRegion(r)}
                      className={[
                        "group relative inline-flex items-center select-none overflow-hidden",
                        "pl-3 pr-10 py-1.5 rounded-full text-xs md:text-lg font-medium",
                        "bg-pink text-black ring-2 transition-colors",
                        active ? "ring-electric/80" : "ring-transparent hover:ring-white/20",
                        active ? "opacity-100" : "opacity-70 hover:opacity-100",
                      ].join(" ")}
                      title={`Hintoja: ${r.count}`}
                    >
                      {r.name} ({r.count})
                      <span
                        role="button"
                        aria-label={`Poista maakunta ${r.name}`}
                        title="Poista maakunta"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeRegionCompletely(r);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            removeRegionCompletely(r);
                          }
                        }}
                        className={[
                          "absolute inset-y-0 right-1.5 my-auto",
                          "flex items-center justify-center",
                          "h-6 w-6 md:h-7 md:w-7 rounded-full",
                          "ring-inset ring-1 ring-black/20 dark:ring-white/20",
                          "bg-electric/40 dark:bg-white/15 text-white font-semibold dark:text-black",
                          "hover:bg-electric/90 dark:hover:bg-white/70",
                          "focus:outline-none focus:ring-2 focus:ring-white/50",
                        ].join(" ")}
                      >
                        ×
                      </span>
                    </button>
                  );
                })
              ) : (
                <span className="text-xs md:text-lg opacity-70">
                  Ei vielä yhtään maakuntaa, jossa olisi hintaa.
                </span>
              )}
            </div>
          </div>

          {/* Setti-dropdown – vain desktopille vasemmalla */}
          <div className="hidden md:block md:justify-self-start">
            <SetSelector
              existingLabels={existingLabelsRegion}
              onPick={handlePickSet}
              loadOptions={loadSetOptions}
            />
          </div>
        </div>

        {/* Oikea sarake: kartta (+ mobiilin setti-dropdown) */}
        <div>
          {/* Suomen kartta – klikkaamalla vaihtuu sama selectedRegion */}
          <FinlandRegionMap
            regions={regions}
            selectedRegionId={selectedRegion?.id ?? null}
            onSelectRegion={safePickRegion}
            regionsWithPrices={regionsWithPrices}
            regionPriceMap={priceMap}
          />

          {/* Setti-dropdown kartan alla – vain mobiiliin */}
          <div className="mt-3 md:hidden">
            <SetSelector
              existingLabels={existingLabelsRegion}
              onPick={handlePickSet}
              loadOptions={loadSetOptions}
            />
          </div>
        </div>
      </div>

      {/* Taulukko (desktop) */}
      <div className="hidden md:block">
        <table className="w-full text-xs md:text-lg">
          <thead>
            <tr className="text-left border-b border-black/10 dark:border-white/10">
              <th className="py-2 pr-3">Nimi</th>
              <th className="py-2 pr-3">Hinta (€)</th>
              <th className="py-2 pr-3 w-36">Toiminnot</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(regionPrices).length === 0 && (
              <tr>
                <td className="py-3 pr-3" colSpan={3}>
                  Ei settejä tässä maakunnassa. Lisää yltä.
                </td>
              </tr>
            )}
            {Object.keys(regionPrices).map((computedLabel, i) => (
              <tr
                key={`${computedLabel}_${i}`}
                className="border-b border-black/5 dark:border-white/5"
              >
                <td className="py-2 pr-3">
                  <input
                    className={`${inputCls} h-9 bg-black/5 dark:bg_WHITE/5 cursor-not-allowed`.replace(
                      "WHITE",
                      "white"
                    )}
                    value={computedLabel}
                    readOnly
                    placeholder="esim. 2x45min"
                    tabIndex={-1}
                  />
                </td>
                <td className="py-2 pr-3">
                  <div className="flex items-center gap-2">
                    <input
                      className={`${inputCls} h-9`}
                      value={regionPrices[computedLabel] ?? ""}
                      onFocus={() => {
                        priceEditRef.current[priceKey(computedLabel)] =
                          regionPrices[computedLabel] ?? "";
                      }}
                      onBlur={(e) => {
                        if (skipToastRef.current) {
                          skipToastRef.current = false;
                          return;
                        }
                        const prev = priceEditRef.current[priceKey(computedLabel)];
                        if (prev !== undefined) {
                          notifyPriceChange(computedLabel, prev, e.target.value ?? "");
                          delete priceEditRef.current[priceKey(computedLabel)];
                        }
                        saveNow();
                      }}
                      onChange={(e) => {
                        if (!computedLabel) return;
                        setRegionPrice(computedLabel, e.target.value);
                        saveSoon();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      placeholder={`esim. 950 (max ${MAX_PRICE_EUR})`}
                      inputMode="decimal"
                      disabled={!selectedRegion || !computedLabel}
                    />
                  </div>
                </td>
                <td className="py-2 pr-3">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      aria-label={`Poista setti ${computedLabel} tältä maakunnalta`}
                      title="Poista setti tältä maakunnalta"
                      className={[
                        "inline-flex items-center justify-center",
                        "h-8 w-8 rounded-full",
                        "ring-inset ring-1 ring-black/20 dark:ring-white/20",
                        "bg-electric/40 dark:bg-white/15 text-white font-semibold dark:text-black",
                        "hover:bg-electric/90 dark:hover:bg-white/70",
                        "focus:outline-none focus:ring-2 focus:ring-white/50",
                      ].join(" ")}
                      onClick={() => {
                        const prev = regionPrices[computedLabel] ?? "";
                        skipToastRef.current = true;
                        removeSetFromRegion(computedLabel);
                        notifyPriceChange(computedLabel, prev, "");
                        toast.info("Setti poistettu tältä maakunnalta.", {
                          containerId: "global",
                        });
                      }}
                    >
                      ×
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobiili-listaus */}
      <div className="md:hidden space-y-3">
        {Object.keys(regionPrices).map((computedLabel, i) => (
          <div
            key={`${computedLabel}_${i}`}
            className="rounded-lg ring-1 ring-black/10 dark:ring-white/10 p-3 space-y-3"
          >
            {/* Nimi + Hinta + Poisto samalle riville */}
            <div className="flex gap-3 items-end">
              {/* Nimi */}
              <div className="flex-1">
                <label className={labelCls}>Nimi</label>
                <input
                  className={`${inputCls} bg-black/5 dark:bg-white/5 cursor-not-allowed`}
                  value={computedLabel}
                  readOnly
                  placeholder="esim. 2x45min"
                  tabIndex={-1}
                />
              </div>

              {/* Hinta */}
              <div className="flex-1">
                <label className={labelCls}>Hinta (€)</label>
                <input
                  className={inputCls}
                  value={regionPrices[computedLabel] ?? ""}
                  onChange={(e) => {
                    if (!computedLabel) return;
                    setRegionPrice(computedLabel, e.target.value);
                    saveSoon();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.currentTarget.blur();
                    }
                  }}
                  placeholder={`esim. 950 (max ${MAX_PRICE_EUR})`}
                  inputMode="decimal"
                  disabled={!selectedRegion || !computedLabel}
                />
              </div>

              {/* Poista setti -painike samassa rivissä */}
              <div className="flex-shrink-0 pb-[2px]">
                <button
                  type="button"
                  aria-label={`Poista setti ${computedLabel} tältä maakunnalta`}
                  title="Poista setti tältä maakunnalta"
                  className={[
                    "inline-flex items-center justify-center",
                    "h-8 w-8 rounded-full",
                    "ring-inset ring-1 ring-black/20 dark:ring-white/20",
                    "bg-electric/40 dark:bg-white/15 text-white font-semibold dark:text-black",
                    "hover:bg-electric/90 dark:hover:bg-white/70",
                    "focus:outline-none focus:ring-2 focus:ring-white/50",
                  ].join(" ")}
                  onClick={() => {
                    const prev = regionPrices[computedLabel] ?? "";
                    skipToastRef.current = true;
                    removeSetFromRegion(computedLabel);
                    notifyPriceChange(computedLabel, prev, "");
                    toast.info("Setti poistettu tältä maakunnalta.", {
                      containerId: "global",
                    });
                  }}
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        ))}

        {Object.keys(regionPrices).length === 0 && (
          <div className="text-xs md:text-lg opacity-75">
            Ei settejä tässä maakunnassa. Lisää yltä.
          </div>
        )}
      </div>

      {!exposeSave && (
        <div className="pt-2">
          <NeonButton type="button" size="md" onClick={onSave} disabled={saving}>
            {saving ? "Tallennetaan…" : "Tallenna muutokset"}
          </NeonButton>
        </div>
      )}
    </form>
  );
}

PricingTab.propTypes = {
  band: PropTypes.object.isRequired,
  exposeSave: PropTypes.func,
  onSaved: PropTypes.func,
};
